import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import {
  cancelFloorInwardRequest,
  clearSelectedRequestItems,
  getFloorRequestById,
  updateFloorRequestItems,
} from "../../../store/slices/Factory/newFloorSlice";
import { useEffect, useState } from "react";
import { FloorRequestStatus, IFloorRequest } from "../../../models/floor.model";
import { formatToDDMMYYYY } from "../../../utils/formatDateUtils";
import { usePreventWheelChange } from "../../../utils/usePreventWheelChange";
import FloorRequestStatusBadge from "./FloorStatusBadge";
import toast from "react-hot-toast";
import Button, { ButtonType } from "../../../assets/Button";
import { UNITS } from "../../../models/warehouseRawMaterial.model";
import {
  getDefaultUnitForType,
  getUnitsByType,
} from "../../../utils/warehouse.util";
import Select from "react-select";

interface FloorRequestEditDetailsModalProps {
  onClose: (success: boolean) => void;
  request?: IFloorRequest;
}

// Interface for local item management tracking removals
interface IEditableItem {
  _id: string;
  rawMaterialId?: { name: string };
  requestedQuantity: number;
  fulfilledQuantity: number;
  unit: string;
  status: FloorRequestStatus;
  isRemoved?: boolean;
}

export default function FloorRequestEditDetailsModal({
  onClose,
  request,
}: FloorRequestEditDetailsModalProps) {
  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "42px",
      fontSize: "0.9rem",
      borderRadius: "0.5rem",
      borderColor: "#D1D5DB",
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 12px",
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };
  const dispatch: RootDispatch = useDispatch();
  const handleWheelPrevent = usePreventWheelChange();

  const items = useSelector(
    (state: RootState) => state.newFloor.selectedRequestitems,
  );
  const { rawMaterials } = useSelector((state: RootState) => state.storeInward);
  const loading = useSelector(
    (state: RootState) => state.newFloor.loading.getFloorRequestByIdLoading,
  );
  const { requestNo, requestedBy, createdAt } = request!;

  // Local state to manage live additions, edits, and marked-for-removal items
  const [localItems, setLocalItems] = useState<IEditableItem[]>([]);
  const cancelling = useSelector(
    (state: RootState) =>
      state.newFloor.loading.cancelFloorInwardRequestLoading,
  );
  const saving = useSelector(
    (state: RootState) => state.newFloor.loading.updateFloorRequestItemsLoading,
  );
  const [materialPick, setMaterialPick] = useState({
    id: "",
    quantity: 1,
    unit: "",
  });
  useEffect(() => {
    dispatch(getFloorRequestById({ id: request!._id }));
  }, [dispatch, request]);

  // Sync redux store items to local state on initial load
  useEffect(() => {
    if (items) {
      setLocalItems(
        items.map((item) => ({
          _id: item._id,
          rawMaterialId: item.rawMaterialId,
          requestedQuantity: item.requestedQuantity,
          fulfilledQuantity: item.fulfilledQuantity || 0,
          unit: item.unit,
          status: item.status,
          isRemoved: false,
        })),
      );
    }
  }, [items]);

  // Handle Editing Requested Quantity
  const handleQuantityChange = (id: string, newQty: number) => {
    setLocalItems((prevItems) =>
      prevItems.map((item) => {
        if (item._id === id) {
          // Enforce rule: Cannot edit if status is approved or rejected
          if (item.status === "approved" || item.status === "rejected") {
            toast.error(
              `Cannot edit item because it is already ${item.status}.`,
            );
            return item;
          }
          // Enforce rule: Requested quantity cannot be less than fulfilled quantity
          if (newQty < item.fulfilledQuantity) {
            toast.error(
              `Quantity cannot be lower than fulfilled layout (${item.fulfilledQuantity})`,
            );
            return item;
          }
          return { ...item, requestedQuantity: newQty };
        }
        return item;
      }),
    );
  };

  // Handle Removing Items
  const handleRemoveItem = (id: string) => {
    const targetItem = localItems.find((item) => item._id === id);
    if (!targetItem) return;

    // Enforce rule: Cannot remove if status is approved or rejected
    if (targetItem.status === "approved" || targetItem.status === "rejected") {
      toast.error(
        `Cannot remove item because it is already ${targetItem.status}.`,
      );
      return;
    }
    // Enforce rule: Cannot remove item if fulfilled is not 0
    if (targetItem.fulfilledQuantity > 0) {
      toast.error(
        "Cannot remove an item that has already been partially or fully fulfilled!",
      );
      return;
    }

    setLocalItems((prevItems) =>
      prevItems.map((item) =>
        item._id === id ? { ...item, isRemoved: true } : item,
      ),
    );
  };

  // Handle Undoing a Removal (Add back)
  const handleUndoRemoveItem = (id: string) => {
    setLocalItems((prevItems) =>
      prevItems.map((item) =>
        item._id === id ? { ...item, isRemoved: false } : item,
      ),
    );
  };

  // Save changes and compile active payload array
  const handleSaveChanges = async () => {
    // Generate operational payload comparing active state changes against initial items
    let payload = localItems
      .map((localItem) => {
        const originalItem = items?.find((i) => i._id === localItem._id);

        // --- CASE A: NEW ITEM DETECTED ---
        if (!originalItem) {
          // If the item was added but marked as removed before saving, discard it completely
          if (localItem.isRemoved) return null;

          return {
            rawMaterialId: localItem._id,
            requestedQuantity: localItem.requestedQuantity,
            unit: localItem.unit,
          };
        }

        // --- CASE B: EXISTING ITEM MUTATION ---
        const isQtyChanged =
          localItem.requestedQuantity !== originalItem.requestedQuantity;
        const isRemoved = localItem.isRemoved;

        if (isQtyChanged || isRemoved) {
          return {
            requestItemId: localItem._id,
            requestedQuantity: localItem.requestedQuantity,
            unit: localItem.unit,
            isRemoved: isRemoved || false,
          };
        }
        return null;
      })
      .filter(Boolean); // Filter out unmodified entries

    if (payload.length === 0) {
      toast.error("No modifications detected to save.");
      return;
    }

    try {
      const res = await dispatch(
        updateFloorRequestItems({ id: request!._id, data: payload }),
      ).unwrap();
      onClose(res.success || false);
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleCancelRequest = async () => {
    const res = await dispatch(
      cancelFloorInwardRequest({ id: request!._id }),
    ).unwrap();
    if (res.success) {
      onClose(true);
    }
  };

  const addMaterial = () => {
    if (!materialPick.id) {
      toast.error("Please select a raw material.");
      return;
    }
    if (materialPick.quantity <= 0) {
      toast.error("Quantity must be greater than zero.");
      return;
    }

    const selectedMaterial = rawMaterials.find(
      (rm) => rm._id === materialPick.id,
    );
    if (!selectedMaterial) return;

    const item: IEditableItem = {
      _id: selectedMaterial._id,
      rawMaterialId: { name: selectedMaterial.name },
      requestedQuantity: materialPick.quantity,
      fulfilledQuantity: 0,
      unit: materialPick.unit,
      status: "pending",
      isRemoved: false,
    };
    setLocalItems((prev) => [...prev, item]);
  };
  const activeItems = localItems;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-2 sm:p-4 backdrop-blur-xs">
      <div className="flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 sm:px-6 sm:py-4 bg-gray-50/50">
          <div>
            <h2 className="text-lg font-bold text-gray-900 tracking-tight">
              Edit Floor Request Items
            </h2>
            <p className="text-xs text-gray-500 hidden sm:block">
              Modify requested quantities or remove unfilled items before
              submitting.
            </p>
          </div>
          <button
            onClick={() => {
              dispatch(clearSelectedRequestItems());
              onClose(false);
            }}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors text-xl font-medium"
          >
            &times;
          </button>
        </div>

        {/* Summary Info Strip */}
        <div className="grid grid-cols-2 gap-3 border-b border-gray-100 p-4 sm:p-5 sm:grid-cols-3 bg-white">
          <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Request No
            </p>
            <p className="font-semibold text-gray-800 text-sm sm:text-base mt-0.5">
              {requestNo}
            </p>
          </div>
          <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Requested By
            </p>
            <p className="font-semibold text-gray-800 text-sm sm:text-base mt-0.5">
              {requestedBy?.username}
            </p>
          </div>
          <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100 col-span-2 sm:col-span-1">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Requested Date
            </p>
            <p className="font-semibold text-gray-800 text-sm sm:text-base mt-0.5">
              {formatToDDMMYYYY(createdAt)}
            </p>
          </div>
        </div>
        {/* Add Material Section */}
        {request?.status !== "approved" && request?.status !== "rejected" && (
          <div className="p-4 bg-green-50/50 rounded-xl border-b border-green-100">
            <label className="block text-[10px] font-bold text-green-800 mb-3 uppercase tracking-widest">
              Add Extra Raw Materials to the batch
            </label>
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-[2] w-full">
                <Select
                  options={rawMaterials.map((rm) => ({
                    value: rm._id,
                    label: rm.name,
                    unitType: rm.unitType,
                  }))}
                  onChange={(opt: any) => {
                    setMaterialPick({
                      id: opt?.value || "",
                      quantity: 1,
                      unit: opt ? getDefaultUnitForType(opt.unitType) : "",
                    });
                  }}
                  value={
                    rawMaterials.find((m) => m._id === materialPick.id)
                      ? {
                          value: materialPick.id,
                          label: rawMaterials.find(
                            (m) => m._id === materialPick.id,
                          )?.name,
                        }
                      : null
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  placeholder="Select Material"
                />
              </div>
              <div className="flex gap-3 w-full md:w-auto">
                <div className="w-24 shrink-0">
                  <input
                    type="number"
                    min={1}
                    onWheel={handleWheelPrevent}
                    value={materialPick.quantity}
                    onChange={(e) =>
                      setMaterialPick((p) => ({
                        ...p,
                        quantity: +e.target.value,
                      }))
                    }
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg h-[42px] outline-none"
                  />
                </div>
                <div className="flex-1 min-w-[160px]">
                  <Select
                    options={
                      materialPick.id
                        ? getUnitsByType(
                            rawMaterials.find((m) => m._id === materialPick.id)
                              ?.unitType || "",
                          )
                        : UNITS
                    }
                    value={
                      UNITS.find((u) => u.value === materialPick.unit) || null
                    }
                    onChange={(opt: any) =>
                      setMaterialPick((p) => ({ ...p, unit: opt?.value || "" }))
                    }
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    placeholder="Unit"
                  />
                </div>
              </div>
              <Button
                type={ButtonType.Submit}
                onClick={addMaterial}
                className="text-xs h-[42px] px-8 w-full md:w-auto shrink-0"
              >
                Add
              </Button>
            </div>
          </div>
        )}
        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-gray-50/30">
          {loading ? (
            <div className="flex justify-center items-center py-12 text-gray-500 text-sm">
              Loading details...
            </div>
          ) : activeItems.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400 font-medium bg-white border-b">
              No items found.
            </div>
          ) : (
            <>
              {/* Desktop View */}
              <table className="hidden md:table w-full border-collapse text-left text-sm bg-white">
                <thead className="sticky top-0 bg-gray-100 text-gray-600 font-medium border-b border-gray-200 shadow-xs z-10">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Material</th>
                    <th className="px-3 py-3 text-right font-semibold w-40">
                      Requested Qty
                    </th>
                    <th className="px-3 py-3 text-right font-semibold">
                      Fulfilled Qty
                    </th>
                    <th className="px-3 py-3 text-center font-semibold">
                      Unit
                    </th>
                    <th className="px-3 py-3 text-center font-semibold">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center font-semibold w-24">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {activeItems.map((item) => (
                    <tr
                      key={item._id}
                      className={`transition-colors ${item.isRemoved ? "bg-red-50/40 line-through opacity-60" : "hover:bg-gray-50/70"}`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {item.rawMaterialId?.name}
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="number"
                          onWheel={handleWheelPrevent}
                          disabled={
                            item.isRemoved ||
                            item.status === "approved" ||
                            item.status === "rejected"
                          }
                          min={item.fulfilledQuantity}
                          value={item.requestedQuantity}
                          onChange={(e) =>
                            handleQuantityChange(
                              item._id,
                              Number(e.target.value),
                            )
                          }
                          className={`w-full rounded-md border px-2 py-1 text-right text-sm font-medium outline-hidden transition-all ${
                            item.status === "approved" ||
                            item.status === "rejected"
                              ? "bg-slate-100 text-slate-500 cursor-not-allowed"
                              : "bg-white"
                          }`}
                        />
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600 font-semibold">
                        {item.fulfilledQuantity}
                      </td>
                      <td className="px-3 py-3 text-center text-gray-500 font-medium">
                        {item.unit}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <FloorRequestStatusBadge
                          status={item.isRemoved ? "removed" : item.status}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.isRemoved ? (
                          <button
                            onClick={() => handleUndoRemoveItem(item._id)}
                            className="text-xs text-blue-600 hover:underline font-semibold"
                          >
                            Undo
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRemoveItem(item._id)}
                            disabled={item.fulfilledQuantity > 0}
                            className="text-xs text-rose-600 hover:text-rose-800 font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
                            title={
                              item.fulfilledQuantity > 0
                                ? "Cannot remove items with active fulfillments"
                                : "Remove item"
                            }
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card View */}
              <div className="block md:hidden p-3 space-y-3">
                {activeItems.map((item) => (
                  <div
                    key={item._id}
                    className={`rounded-xl border p-3.5 shadow-xs space-y-3 transition-all ${
                      item.isRemoved
                        ? "border-red-200 bg-red-50/10 opacity-60 line-through"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm leading-tight">
                          {item.rawMaterialId?.name}
                        </h4>
                        <span className="text-[11px] font-medium text-gray-500 mt-1 inline-block bg-gray-100 rounded px-1.5 py-0.2">
                          Unit: {item.unit}
                        </span>
                      </div>
                      <FloorRequestStatusBadge
                        status={item.isRemoved ? "cancelled" : item.status}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100 items-center">
                      <div>
                        <span className="block text-[10px] font-medium text-gray-400 uppercase">
                          Fulfilled Qty
                        </span>
                        <span className="text-xs font-bold text-gray-700">
                          {item.fulfilledQuantity}
                        </span>
                      </div>
                      <div>
                        <span className="block text-[10px] font-medium text-gray-400 uppercase mb-1 text-right">
                          Requested Qty
                        </span>
                        <input
                          type="number"
                          onWheel={handleWheelPrevent}
                          disabled={
                            item.isRemoved ||
                            item.status === "approved" ||
                            item.status === "rejected"
                          }
                          min={item.fulfilledQuantity}
                          value={item.requestedQuantity}
                          onChange={(e) =>
                            handleQuantityChange(
                              item._id,
                              Number(e.target.value),
                            )
                          }
                          className={`w-full rounded-md border px-2 py-1 text-right text-sm font-medium outline-hidden transition-all ${
                            item.status === "approved" ||
                            item.status === "rejected"
                              ? "bg-slate-100 text-slate-500 cursor-not-allowed"
                              : "bg-white"
                          }`}
                        />
                      </div>
                    </div>

                    <div className="pt-2 text-right border-t border-gray-50">
                      {item.isRemoved ? (
                        <button
                          onClick={() => handleUndoRemoveItem(item._id)}
                          className="text-xs text-blue-600 font-bold"
                        >
                          Undo Removal
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRemoveItem(item._id)}
                          disabled={
                            item.fulfilledQuantity > 0 ||
                            item.status === "approved" ||
                            item.status === "rejected"
                          }
                          className="text-xs text-rose-600 font-bold disabled:opacity-30"
                        >
                          Remove Item
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Action Footer */}
        <div className="border-t border-gray-100 bg-gray-50 p-4 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <p className="text-xs text-gray-500 font-medium text-center sm:text-left">
            Review your adjustments before committing changes.
          </p>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={() => onClose(false)}
              className="w-1/2 sm:w-auto px-5 py-2 rounded-lg border border-gray-300 text-gray-700 text-xs sm:text-sm font-semibold hover:bg-gray-100 transition-all cursor-pointer"
            >
              Cancel
            </button>
            {(request!.status === "pending" ||
              request!.status === "partially fulfilled") && (
              <button
                onClick={handleCancelRequest}
                disabled={
                  loading || saving || cancelling || localItems.length === 0
                }
                className="w-full sm:w-auto px-5 py-2 rounded-lg bg-rose-600 text-white text-xs sm:text-sm font-semibold tracking-wide hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition-all cursor-pointer"
              >
                Cancel Request
              </button>
            )}
            <button
              onClick={handleSaveChanges}
              disabled={
                loading || saving || cancelling || localItems.length === 0
              }
              className="w-1/2 sm:w-auto px-5 py-2 rounded-lg bg-blue-600 text-white text-xs sm:text-sm font-semibold tracking-wide hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition-all cursor-pointer"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
